 
import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  EditableTable,
  FileDropZone,
  FilePresent,
  OrDivider,
  PageTitleAndSubTitle,
  RadioButton,
  TypableDropdown,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { removeEmptyFields } from '../../../core-utils/helper-utils/riskRegisterHelper';
import { financialForensicsConstants } from '../../../core-utils/messages/financialForensicsConstants';
import { editableColumn } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/EditableTable';
import { formatFileSize } from '../../../core-utils/helper-utils/TaskBoardHelper';
import {
  generateDateRanges,
  unitToRowTitleMap,
} from '../../../core-utils/helper-utils/financialForensicsHelper';
import {
  FeeCalculationModalType,
  getEmptyFeeCalculationModalState,
} from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import {
  FixedCostFeeCalculation,
  FixedCostFeeOption,
  emptyFixedCostFeeCalculation,
} from '../../../pages/FinancialForensicsPage/types/FixedCost.types';
import { CountFrequency } from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import { TimeSeriesEntry } from '../../../pages/FinancialForensicsPage/types/EmployeeCount.types';
import { Detail } from '../../../pages/FinancialForensicsPage/types/VariableCost.types';

export interface FeeCalculationModalProps {
  open: FeeCalculationModalType;
  setOpen: React.Dispatch<React.SetStateAction<FeeCalculationModalType>>;
  sourceOfDataOptions: string[];
   
  updateFieldNamesForSourceOfData: (value: string, field: string) => void;
  handleSubmit: (
     
    data: any,
     
    serviceID: string,
     
    vendorID: string,
     
    rowType: string,
  ) => void;
  onClose: () => void;
  handleOpenSnackBar: any;
  startDate: string;
  endDate: string;
  dataFromProps?: FixedCostFeeCalculation;
}

interface AmountTypeOption {
  name: FixedCostFeeOption;
  checked: boolean;
}

interface DetailOption {
  name: CountFrequency;
  checked: boolean;
}

const INITIAL_AMOUNT_TYPE_OPTIONS: AmountTypeOption[] = [
  { name: 'Enter Breakdown', checked: false },
  { name: 'Enter Total Fee', checked: false },
];

const INITIAL_DETAIL_OPTIONS: DetailOption[] = [
  { name: 'Monthly', checked: false },
  { name: 'Quarterly', checked: false },
  { name: 'Annually', checked: false },
];

const FeeCalculationModal: React.FC<FeeCalculationModalProps> = observer(
  ({
    open,
    setOpen,
    sourceOfDataOptions,
    updateFieldNamesForSourceOfData,
    onClose,
    handleSubmit,
    startDate,
    endDate,
    dataFromProps,
  }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '1203px',
        height: '760px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: { padding: '0px' },
      scroll: scrollBarStyles,
      flex: {
        display: 'flex',
        gap: '100px',
        flexWrap: 'wrap',
        alignItems: 'start',
        margin: '0px 16px 20px 0px',
      },
      service: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        padding: '20px 20px 15px 20px',
        height: 'fit-content',
      },
      content: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        overflowY: 'scroll',
        height: '588px',
      },
    });

    const { classes } = useStyles();

    const [amountTypeOptions, setAmountTypeOptions] = useState<
      AmountTypeOption[]
    >(INITIAL_AMOUNT_TYPE_OPTIONS);
    const [detailOptions, setDetailOptions] = useState<DetailOption[]>(
      INITIAL_DETAIL_OPTIONS,
    );
    const [data, setData] = useState<FixedCostFeeCalculation>(
      emptyFixedCostFeeCalculation(),
    );
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

    const isCommonFieldsFilled =
      !!data.source &&
      !!data.option &&
      !!data.detailsType &&
      data.details.length > 0 &&
      data.details.every((item) => item.type && item.values.length > 0);

    const clearData = () => {
      setAmountTypeOptions(INITIAL_AMOUNT_TYPE_OPTIONS);
      setDetailOptions(INITIAL_DETAIL_OPTIONS);
      setData(emptyFixedCostFeeCalculation());
      setUploadedFiles([]);
    };

    const handleClose = () => {
      clearData();
      onClose();
      setOpen(getEmptyFeeCalculationModalState());
    };

    const handleSaveOrSubmit = () => {
      const cleanedData = removeEmptyFields(data);
      handleSubmit(
        { ...cleanedData, files: uploadedFiles },
        open.selectedServiceID,
        open.selectedVendorID,
        open.type,
      );
      handleClose();
    };

    const handleAmountTypeChange = (index: number) => {
      const updatedOptions = amountTypeOptions.map((opt, i) => ({
        ...opt,
        checked: i === index,
      }));
      setAmountTypeOptions(updatedOptions);
      setDetailOptions(INITIAL_DETAIL_OPTIONS);

      const selected = updatedOptions[index];
      if (!selected.checked) {
        return;
      }

      const seedDetail: Detail =
        selected.name === 'Enter Breakdown'
          ? {
              type: unitToRowTitleMap[open.unitOfFees] || 'Fee Value',
              values: [],
            }
          : { type: 'Total Fee', values: [] };

      setData((prev) => ({
        ...prev,
        option: selected.name,
        detailsType: undefined,
        details: [seedDetail],
      }));
    };

    // Changing detailsType regenerates values for all detail rows
    const handleDetailsOptionChange = (index: number) => {
      const updatedOptions = detailOptions.map((opt, i) => ({
        ...opt,
        checked: i === index,
      }));
      setDetailOptions(updatedOptions);

      const selected = updatedOptions[index];
      if (!selected.checked) {
        return;
      }

      const headers = generateDateRanges(selected.name, startDate, endDate);
      const values: TimeSeriesEntry[] = headers.map((dateRange) => ({
        dateRange,
        value: null,
      }));

      setData((prev) => ({
        ...prev,
        detailsType: selected.name as CountFrequency,
        details: prev.details.map((detail) => ({ ...detail, values })),
      }));
    };

    // ─── Table Helpers ────────────────────────────────────────────────

    const getColumnsForTable = (): editableColumn[] => {
      const firstDetail = data.details[0];
      if (!firstDetail?.values?.length) {
        return [];
      }

      const firstColumn: editableColumn = {
        key: 'type',
        label: 'Type',
        editable: false,
        width: '150px',
      };

      const dateRangeColumns: editableColumn[] = firstDetail.values.map(
        (entry) => ({
          key: entry.dateRange,
          label: entry.dateRange,
          editable: true,
          type: 'number',
        }),
      );

      return [firstColumn, ...dateRangeColumns];
    };

    const getTableData = () =>
      data.details.map((detail) => {
        const row: Record<string, string | number | null> = {
          type: detail.type,
        };
        detail.values.forEach((entry) => {
          row[entry.dateRange] = entry.value;
        });
        return row;
      });

    const handleTableDataChange = (updatedRows: any[]) => {
      const updatedDetails: Detail[] = updatedRows.map((row, index) => ({
        type: row.type ?? data.details[index]?.type ?? '',
        values: Object.entries(row)
          .filter(([key]) => key !== 'type')
          .map(([dateRange, value]) => ({
            dateRange,
            value: value as number | null,
          })),
      }));

      setData((prev) => ({
        ...prev,
        details: updatedDetails,
      }));
    };

    // ─── Effects ─────────────────────────────────────────────────────

    useEffect(() => {
      if (!dataFromProps?.detailsType) {
        return;
      }

      const updatedAmountType = INITIAL_AMOUNT_TYPE_OPTIONS.map((opt) => ({
        ...opt,
        checked: opt.name === dataFromProps.option,
      }));
      const updatedDetailsOpt = INITIAL_DETAIL_OPTIONS.map((opt) => ({
        ...opt,
        checked: opt.name === dataFromProps.detailsType,
      }));

      setAmountTypeOptions(updatedAmountType);
      setDetailOptions(updatedDetailsOpt);

      const headers = generateDateRanges(
        dataFromProps.detailsType,
        startDate,
        endDate,
      );

      const normalizedDetails: Detail[] = dataFromProps.details.map((row) => ({
        type: row.type,
        values: headers.map((dateRange) => ({
          dateRange,
          value:
            row.values.find((v) => v.dateRange === dateRange)?.value ?? null,
        })),
      }));

      setData({ ...dataFromProps, details: normalizedDetails });
    }, [dataFromProps]);

    return (
      <Modal open={open.open} onClose={handleClose} className={classes.modal}>
        <Paper className={classes.paper}>
          <Grid
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '30px',
            }}
          >
            <PageTitleAndSubTitle
              title={financialForensicsConstants.feeCalculation}
              subTitle={''}
              subTitleStyles={{ width: '0px' }}
            />
            <SvgClose
              style={{ cursor: 'pointer' }}
              color={patientAccessTheme.palette.status.icon}
              onClick={handleClose}
            />
          </Grid>

          <Grid className={`${classes.scroll} ${classes.content}`}>
            <Grid className={`${classes.flex} ${classes.service}`}>
              {/* Source of Data */}
              <ReportCreateNewLabelField
                label={financialForensicsConstants.sourceOfData}
                component={
                  <TypableDropdown
                    dropDownOptions={sourceOfDataOptions}
                    dropDownValues={sourceOfDataOptions}
                    placeholder=""
                    value={data.source}
                    handleChange={(event: any) =>
                      setData((prev) => ({
                        ...prev,
                        source: event.target.value,
                      }))
                    }
                    handleInputChange={(value: string) => {
                      updateFieldNamesForSourceOfData(value, 'source_of_data');
                      setData((prev) => ({ ...prev, source: value }));
                    }}
                    error={!data.source}
                    width="500px"
                    textFieldPlaceholder="Add Source of Data"
                  />
                }
              />

              {/* Amount Type Selector */}
              <ReportCreateNewLabelField
                label={financialForensicsConstants.amountTypeQuestion}
                component={
                  <div style={{ display: 'flex', gap: '20px' }}>
                    {amountTypeOptions.map((option, index) => (
                      <label
                        key={option.name}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <RadioButton
                          color={patientAccessTheme.palette.primary[400]}
                          checked={option.checked}
                          handleChange={() => handleAmountTypeChange(index)}
                        />
                        <span style={{ marginLeft: '6px' }}>
                          <Typography
                            textColor={
                              patientAccessTheme.palette.text.highemphasis
                            }
                            fontSize="12px"
                            lineHeight="12px"
                            fontType="semiBold"
                          >
                            {option.name}
                          </Typography>
                        </span>
                      </label>
                    ))}
                  </div>
                }
              />
            </Grid>

            {data.details.length > 0 && (
              <>
                <Typography
                  textColor={patientAccessTheme.palette.text.highemphasis}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.detailsQuestion}
                </Typography>

                <div
                  style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}
                >
                  {detailOptions.map((option, index) => (
                    <label
                      key={option.name}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <RadioButton
                        color={patientAccessTheme.palette.primary[400]}
                        checked={option.checked}
                        handleChange={() => handleDetailsOptionChange(index)}
                      />
                      <span style={{ marginLeft: '6px' }}>
                        <Typography
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                          fontSize="12px"
                          lineHeight="12px"
                          fontType="semiBold"
                        >
                          {option.name}
                        </Typography>
                      </span>
                    </label>
                  ))}
                </div>

                {data.detailsType && (
                  <Grid style={{ marginRight: '20px' }}>
                    <EditableTable
                      data={getTableData()}
                      columns={getColumnsForTable()}
                      onChange={handleTableDataChange}
                      containerStyles={{
                        overflow: 'visible',
                        height: 'auto',
                        overflowY: 'visible',
                        width: '100%',
                      }}
                      bodyCellStyles={{
                        padding: '0.5rem',
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                )}
              </>
            )}

            <OrDivider />

            <Typography
              textColor={patientAccessTheme.palette.text.highemphasis}
              fontSize="12px"
              lineHeight="12px"
              fontType="medium"
            >
              {financialForensicsConstants.upload}
            </Typography>
            <FileDropZone
              filesSupported={['.pdf', '.doc', '.docx']}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              isMultiple={true}
              dragDropAreaStyles={{ marginRight: '20px', width: '98%' }}
            />
            {uploadedFiles.map((file, index) => (
              <UploadedDocument
                key={index}
                width="592px"
                name={file.name}
                truncateWidth="420px"
                size={formatFileSize(file.size)}
                startIcon={
                  <FilePresent
                    stroke={patientAccessTheme.palette.primary[500]}
                    fill={patientAccessTheme.palette.primary[500]}
                  />
                }
                handleDelete={() =>
                  setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
                }
              />
            ))}
          </Grid>

          <Grid
            style={{
              display: 'flex',
              justifyContent: 'right',
              margin: '0px 40px 40px 0px',
              position: 'absolute',
              right: '0px',
              bottom: '0px',
            }}
          >
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.primary.main}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.Save_update}
                </Typography>
              }
              variant="outlined"
              height="36px"
              width="180px"
              styleProps={{ marginRight: '25px' }}
              borderRadius="4px"
              className={classes.button}
              onClick={handleSaveOrSubmit}
            />
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.status.white}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.submit}
                </Typography>
              }
              variant="contained"
              color="primary"
              height="36px"
              width="180px"
              borderRadius="4px"
              className={classes.button}
              onClick={handleSaveOrSubmit}
              disabled={!isCommonFieldsFilled}
            />
          </Grid>
        </Paper>
      </Modal>
    );
  },
);

export default FeeCalculationModal;
